import {
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  IconButton,
  Box,
  Stack,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import PropTypes from 'prop-types';
import Iconify from 'src/components/iconify';
import {
  getOrganizationDetails,
  getOrganizationId,
  sendPricingRequest,
} from 'src/services/user.service';
import Animation from 'src/assets/animations/components/animation-wrapper';
import successDataAnimation from 'src/assets/animations/json/success-tick.json';
import { useEffect, useState } from 'react';
import { useAuthContext } from 'src/auth/hooks';

const plans = {
  starter: {
    title: 'Starter (1 Job)',
    features: [
      '1 Job for up to 1 month',
      'Job Application Page',
      'Unlimited Applications',
      'Unlimited AI Pre-screening and Screening',
      'Unlimited AI Interviews',
      'AI Automated Workflows',
      '1 Admin Account',
      'Apply-to-Hired Tracking System',
      'Email Support',
    ],
    button: 'Buy Now',
  },
  enterprise: {
    title: 'Enterprise',
    features: [
      'Unlimited Jobs',
      'Job Application Page',
      'Career Page',
      'Unlimited Applications',
      'Unlimited AI Pre-screening and Screening',
      'Unlimited AI Interviews',
      'AI Automated Workflows',
      'Unlimited Admin Accounts',
      'Apply-to-Hired Tracking System',
      'Dedicated Account Manager',
      'Custom Integration',
    ],
    button: 'Contact Us',
  },
};

export default function PricingPlansDialog({
  open,
  onClose,
  setOpenPricingPlans,
}) {
  // context
  const { organization } = useAuthContext();

  // states
  const [isSubmittingPricingRequest, setIsSubmittingPricingRequest] =
    useState('');

  const [isSubmittedPricingRequest, setIsSubmittedPricingRequest] =
    useState(false);

  // functions
  const handleOnClose = () => {
    setOpenPricingPlans(false);
    setIsSubmittingPricingRequest('');
    onClose();
  };

  // components
  const PricingSection = (
    <Stack
      direction="row"
      spacing="18px"
      alignItems="center"
      height="100%"
      justifyContent="center"
    >
      {Object.keys(plans).map((plan, index) =>
        organization &&
        organization.country_code &&
        organization.country_code !== 'IN' &&
        plan === 'starter' ? null : (
          <Box
            key={`${index + 1}pricing-plan`}
            sx={{
              border: theme =>
                `1px solid ${theme.palette.border.primaryBlack3}`,
              bgcolor: theme => theme.palette.background.primaryWhite1,
              borderRadius: '12px',
              px: '16px',
              py: '20px',
              width: '460px',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              position: 'relative',
            }}
          >
            <Stack mb="21px">
              <Typography variant="subheading2">{plans[plan].title}</Typography>
            </Stack>
            <Stack justifyContent="space-between" height="100%" spacing="20px">
              <Stack spacing="12px">
                {plans[plan].features.map(feature => (
                  <Typography key={feature} variant="input" fontWeight={500}>
                    {feature}
                  </Typography>
                ))}
              </Stack>
              <Stack>
                <LoadingButton
                  loading={isSubmittingPricingRequest === plan}
                  onClick={() => {
                    if (plan === 'starter') {
                      window.open('https://rzp.io/l/hireintel', '_blank');
                    } else {
                      setIsSubmittingPricingRequest(plan);
                      sendPricingRequest(plan)
                        .then(() => {
                          setIsSubmittedPricingRequest(true);
                        })
                        .catch(err => {
                          console.log('error: ', err);
                        })
                        .finally(() => {
                          setIsSubmittingPricingRequest('');
                        });
                    }
                  }}
                  sx={{
                    bgcolor: theme =>
                      theme.palette.background[
                        plan === 'starter' ? 'primaryAccent1' : 'primaryBlack1'
                      ],
                  }}
                  variant="contained"
                >
                  <Typography variant="caption">
                    {plans[plan].button}
                  </Typography>
                </LoadingButton>
              </Stack>
            </Stack>
          </Box>
        ),
      )}
    </Stack>
  );

  const SuccessMessage = (
    <Stack
      spacing="40px"
      alignItems="center"
      justifyContent="center"
      height="100%"
    >
      <Animation animationData={successDataAnimation} />
      <Typography
        color={theme => theme.palette.content.primaryBlack2}
        variant="h4"
      >
        Thank you for your request we will contact you soon
      </Typography>
    </Stack>
  );

  // effects
  useEffect(() => {
    if (!open) {
      setTimeout(() => {
        setIsSubmittedPricingRequest(false);
      }, 400);
    }
  }, [open]);

  return (
    <Dialog open={open} onClose={handleOnClose} maxWidth="md">
      <DialogTitle>
        <Typography variant="h4">Upgrade plan</Typography>
      </DialogTitle>
      <IconButton
        sx={{
          position: 'absolute',
          top: '16px',
          right: '16px',
        }}
        onClick={handleOnClose}
      >
        <Iconify icon="tabler:x" />
      </IconButton>
      <DialogContent
        sx={{
          pb: '36px',
          height: '80vh',
          width: '800px',
        }}
      >
        {isSubmittedPricingRequest ? SuccessMessage : PricingSection}
      </DialogContent>
    </Dialog>
  );
}

PricingPlansDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  setOpenPricingPlans: PropTypes.func.isRequired,
  onClose: PropTypes.func,
};
